import { DateTime } from 'luxon';
import { store } from '../redux/store';
import location from './location';
import { segment } from './segment';
import { sentryTrack } from './sentryEventLogger';
import { selectors as clientSelectors } from 'redux/client/client.slice';
import * as Sentry from '@sentry/react';
import { analyticsEnabledByEnv } from './analyticsEnabledByEnv';
import { heap } from './heap';

/**
 * This is a wrapper around our chosen analytics platform in case we want to
 * swap it out for something else later on. It also allows for a much cleaner
 * impelementation in the areas of the code where we actually trigger these
 * events.
 */
export const incAnalytics = {
  identify: async userData => {
    const data = identifyTransform(userData);
    segment.identify(userData?.userId, data);
    await heap.identify(userData?.userId);
    heap.addUserProperties(data);
  },
  identifyGuest: async traits => {
    segment.identify(traits);
    await heap.identify(traits?.email);
    heap.addUserProperties(traits);
  },

  // Segment stores identity independently of our redux state, so signing out
  // has to clear it explicitly or the next guest checkout in this browser
  // identifies as the user who just left. Only the person changed, not the
  // device or the tenant, so the anonymousId and group are left alone.
  clearIdentity: () => segment.clearUserIdentity(),

  page: (...args) => segment.page(...args),

  // Segment keeps one identity per browser, on a cookie scoped to the
  // top-level domain, so every client we serve from a host shares it —
  // including across /c/{clientAlias}. The group id is our tenant marker, and
  // it lives in the same stores as that identity, so it has exactly the same
  // reach and lifetime as the thing it guards. When it names a different
  // client than the one loading, the stored user identity belongs to that
  // other client and must not follow the visitor here, or their PII ends up
  // merged onto someone else's profile in this client's Segment source.
  //
  // Clearing has to happen before the group call below overwrites the marker.
  //
  // There must also be exactly one group call per page load: changing the
  // group id resets the anonymousId (see User.id in analytics-next), so a
  // second call churns the anonymous identity on every load.
  clientGroup: async (clientId, clientAlias) => {
    const previousClientId = await segment.getGroupId();

    if (!!previousClientId && previousClientId !== clientId) {
      await segment.clearUserIdentity();
    }

    return segment.group(clientId, {
      name: clientAlias,
      description: 'Incentivio Client Mapping',
    });
  },

  trackSignIn: properties => {
    segment.track('Signed In', properties, userContext());
    heap.track('Signed In', properties, userContext());
    sentryTrack('Signed In', properties);
  },
  trackSignOut: properties => {
    segment.track('Signed Out', properties, userContext());
    heap.track('Signed Out', properties, userContext());
    sentryTrack('Signed Out', properties);
  },
  trackSignUp: properties => {
    segment.track('Signed Up', properties, userContext());
    heap.track('Signed Up', properties, userContext());
    sentryTrack('Signed Up', properties);
  },

  trackOrderCompleted: (order, storeTitle) => {
    const data = {
      order_id: order?.orderId,
      affiliation: storeTitle,
      subtotal: order?.orderTotal.subtotal / 1000,
      total: order?.orderTotal.total / 1000,
      revenue: order?.orderTotal.subtotal / 1000,
      shipping: order?.orderTotal.chargeSummary.totalCharges / 1000,
      tax: order?.orderTotal.taxSummary.totalTaxes / 1000,
      discount: order?.orderTotal.totalDiscountApplied / 1000,
      coupon: order?.orderDiscounts?.[0]?.description,
      currency: 'USD',
      products: order?.orderItems?.map(orderItem => itemToProduct(orderItem)),
    };
    segment.track('Order Completed', data);
    heap.track('Order Completed', data);
    sentryTrack('Order Completed', data);
  },

  trackProductAdded: orderItem => {
    const data = itemToProduct(orderItem);
    segment.track('Product Added', data);
    heap.track('Product Added', data);
    sentryTrack('Product Added', data);
  },

  trackProductUpdated: orderItem => {
    const data = itemToProduct(orderItem);
    segment.track('Product Updated', data);
    heap.track('Product Updated', data);
    sentryTrack('Product Updated', data);
  },

  trackProductDeleted: orderItem => {
    const data = itemToProduct(orderItem);
    segment.track('Product Removed', data);
    heap.track('Product Removed', data);
    sentryTrack('Product Removed', data);
  },

  trackProductViewed: item => {
    const data = itemToProduct(item, {
      price: item?.price / 1000,
      url: location.getLocationHref(),
    });
    segment.track('Product Viewed', data);
    heap.track('Product Viewed', data);
    sentryTrack('Product Viewed', data);
  },

  trackRecommendationAdded: orderItem => {
    const data = itemToProduct(orderItem);
    segment.track('Recommendation Added', data);
    heap.track('Recommendation Added', data);
    sentryTrack('Recommendation Added', data);
  },

  trackRecommendationRemoved: orderItem => {
    const data = itemToProduct(orderItem);
    segment.track('Recommendation Removed', data);
    heap.track('Recommendation Removed', data);
    sentryTrack('Recommendation Removed', data);
  },

  trackPaymentInfoEntered: payInfo => {
    const data = {
      order_id: payInfo?.orderId,
      step: undefined,
      shipping_method: undefined,
      payment_method: undefined,
    };
    segment.track('Payment Info Entered', data);
    heap.track('Payment Info Entered', data);
    sentryTrack('Payment Info Entered', data);
  },

  trackCartViewed: orderItems => {
    const data = {
      products: orderItems?.map(orderItem => itemToProduct(orderItem)),
    };
    segment.track('Cart Viewed', data);
    heap.track('Cart Viewed', data);
    sentryTrack('Cart Viewed', data);
  },

  trackRecommendationsViewed: orderItems => {
    const data = {
      products: orderItems?.map(orderItem =>
        itemToProduct(orderItem, { price: orderItem?.price / 1000 }),
      ),
    };
    segment.track('Recommendations Viewed', data);
    heap.track('Recommendations Viewed', data);
    sentryTrack('Recommendations Viewed', data);
  },

  trackCouponEntered: (data = {}) => {
    const couponData = {
      order_id: data.orderId,
      coupon_id: data.promoCode,
    };
    segment.track('Coupon Entered', couponData);
    heap.track('Coupon Entered', couponData);
    sentryTrack('Coupon Entered', couponData);
  },

  trackCouponApplied: (data = {}) => {
    const couponData = {
      order_id: data.orderId,
      coupon_id: data.offerId,
      coupon_name: data.title,
      discount: data.discount / 1000,
    };
    segment.track('Coupon Applied', couponData);
    heap.track('Coupon Applied', couponData);
    sentryTrack('Coupon Applied', couponData);
  },

  trackCouponDenied: (data = {}) => {
    const couponData = {
      order_id: data.orderId,
      coupon_id: data.offerId,
      coupon_name: data.title,
      reason: data.reason,
    };
    segment.track('Coupon Denied', couponData);
    heap.track('Coupon Denied', couponData);
    sentryTrack('Coupon Denied', couponData);
  },

  trackCouponRemoved: (data = {}) => {
    const couponData = {
      order_id: data.orderId,
      coupon_id: data.offerId,
      coupon_name: data.title,
      discount: data.discount,
    };
    segment.track('Coupon Removed', couponData);
    heap.track('Coupon Removed', couponData);
    sentryTrack('Coupon Removed', couponData);
  },

  trackTipApplied: (data = {}) => {
    const couponData = {
      tip: data.tip,
      currency: 'USD',
    };
    segment.track('Tip Applied', couponData);
    heap.track('Tip Applied', couponData);
    sentryTrack('Tip Applied', couponData);
  },

  trackCheckoutStarted: (order, storeTitle) => {
    const data = {
      order_id: order?.orderId,
      affiliation: storeTitle,
      subtotal: order?.orderTotal?.subtotal / 1000,
      total: order?.orderTotal?.total / 1000,
      revenue: order?.orderTotal?.subtotal / 1000,
      shipping: order?.orderTotal?.chargeSummary?.totalCharges / 1000,
      tax: order?.orderTotal?.taxSummary?.totalTaxes / 1000,
      discount: order?.orderTotal?.totalDiscountApplied / 1000,
      coupon: order?.orderDiscounts?.[0]?.description,
      currency: 'USD',
      products: order?.orderItems?.map(orderItem => itemToProduct(orderItem)),
    };
    segment.track('Checkout Started', data);
    heap.track('Checkout Started', data);
    sentryTrack('Checkout Started', data);
  },

  trackReorder: (order, storeTitle) => {
    const data = {
      order_id: order?.orderId,
      affiliation: storeTitle,
      subtotal: order?.orderTotal?.subtotal / 1000,
      total: order?.orderTotal?.total / 1000,
      revenue: order?.orderTotal?.subtotal / 1000,
      shipping: order?.orderTotal?.chargeSummary?.totalCharges / 1000,
      tax: order?.orderTotal?.taxSummary?.totalTaxes / 1000,
      discount: order?.orderTotal?.totalDiscountApplied / 1000,
      coupon: order?.orderDiscounts?.[0]?.description,
      currency: 'USD',
      products: order?.orderItems?.map(orderItem => itemToProduct(orderItem)),
    };
    segment.track('Reorder', data);
    heap.track('Reorder', data);
    sentryTrack('Reorder', data);
  },
};

export const identifyTransform = (userData = {}) => ({
  ...(userData.extendedAttributes || {}),
  address: userData.address,
  birthday: DateTime.fromISO(userData.dateOfBirth).toISO(),
  email: userData.email,
  firstName: userData.firstName,
  id: userData.userId,
  lastName: userData.lastName,
  phone: userData.phoneNumber,
  gender: userData.gender,
  avatar: userData.userProfileImageUrl,
  clientId: userData.clientId,
  externalId: userData.externalId,
  howAcquired: userData.howAcquired,
});

const itemToProduct = (item = {}, overrides = {}) => {
  const displayInfo = getDisplayInfo(item);
  const coupon = getItemDiscount(item);

  return {
    product_id: item?.itemId,
    category: item?.group?.groupDisplayName ?? item?.group?.groupName,
    name:
      displayInfo.title ??
      item?.displayInfoTitle ??
      item?.itemName ??
      item?.title,
    price: getItemPrice(item),
    quantity: item.quantity || 1,
    image_url: displayInfo.mediumImage?.[0],
    coupon,
    ...overrides,
  };
};

const getItemPrice = item =>
  item?.itemTotal?.total / 1000 || item?.unitPrice / 1000 || 0;

export const getItemDiscount = item => {
  if (item.itemDiscounts?.length) {
    return item.itemDiscounts[0]?.description;
  } else if (!!item.options?.length) {
    for (let option of item.options) {
      const discount = getItemDiscount(option);
      if (!!discount) {
        return discount;
      }
    }
  }
};

const getDisplayInfo = (item = {}) => {
  if (!!item.displayInfo && item.displayInfo.title) return item.displayInfo;

  if (
    !item.displayInfo ||
    !Array.isArray(item.displayInfo) ||
    item.displayInfo.length < 1
  ) {
    return {};
  }

  // The ?? {} might be redundant with the previous conditional, but I'm not
  // sure. So, I'm keeping it.
  return item?.displayInfo?.[0] ?? {};
};

const userContext = () => ({
  context: {
    groupId: clientSelectors.selectClientId(store.getState()),
  },
});

const fakeAnalytics = {
  ...Object.keys(incAnalytics).reduce((result, key) => {
    result[key] = () => null;
    return result;
  }, {}),
};

const makeWrapper = wrappee => {
  return (...args) => {
    try {
      const result = wrappee(...args);

      // Several of these methods are async, so a rejection would escape the
      // catch below and go unreported. Resolve to undefined rather than to
      // the Sentry event id, so a trapped rejection looks the same to callers
      // as a trapped throw.
      if (typeof result?.then === 'function') {
        return result.catch(error => {
          Sentry.captureException(error);
        });
      }

      return result;
    } catch (error) {
      Sentry.captureException(error);
    }
  };
};

// This prevents any of the analytics methods from throwing an error that will
// crash the app. We will report this to Sentry if it happens.
const analyticsProxy = new Proxy(incAnalytics, {
  get: function (obj, prop) {
    const result = obj[prop];

    if (result instanceof Function) {
      return makeWrapper(result);
    } else {
      // Method does not exist on incAnalytics. Return a method that logs the
      // issue with Sentry
      return () => {
        Sentry.captureException(
          new Error(`Method ${prop} does not exist on incAnalytics`),
        );
      };
    }
  },
});

export const getAnalytics = () =>
  analyticsEnabledByEnv() ? analyticsProxy : fakeAnalytics;
